import React, { useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Title
);

export type SurveyItem = {
  value: string; // etiqueta de la respuesta
  count: number; // cantidad de votos
};

type Props = {
  /** Puede venir con HTML; se sanitiza antes de mostrar/exportar */
  title?: string;
  data: SurveyItem[];
  height?: number;
  showPercentInLegend?: boolean;
  exportFilename?: string;
};

const SurveyResultsChart: React.FC<Props> = ({
  title = "Resultados de la encuesta",
  data,
  height = 320,
  showPercentInLegend = true,
  exportFilename,
}) => {
  const chartRef = useRef<ChartJS | null>(null);
  const [chartType, setChartType] = useState<"bar" | "line" | "pie">("bar");

  // Sanitizar el título (puede venir con etiquetas HTML o imágenes)
  const stripHtml = (s?: string) => {
    if (!s) return "";
    try {
      const doc = new DOMParser().parseFromString(s, "text/html");
      return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    } catch {
      return (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
  };

  const sanitizedTitle = stripHtml(title) || "Resultados de la encuesta";

  const fileBase =
    (exportFilename || sanitizedTitle || "encuesta").replace(/[^\p{L}\p{N}\-_ ]/gu, "").trim() || "encuesta";

  // ===== Datos y métricas =====
  const { labels, counts, total, rows, top } = useMemo(() => {
    const safe = (data ?? [])
      .filter((d) => d && typeof d.value === "string" && typeof d.count === "number")
      .map((d) => ({ value: d.value.trim() || "Sin respuesta", count: Math.max(0, d.count) }));

    safe.sort((a, b) => a.value.localeCompare(b.value, "es"));
    const labels = safe.map((d) => stripHtml(d.value));
    const counts = safe.map((d) => d.count);
    const total = counts.reduce((a, b) => a + b, 0);

    const rows = safe.map((d) => ({
      value: stripHtml(d.value),
      count: d.count,
      percent: total ? (d.count * 100) / total : 0,
    }));

    const top = rows.slice().sort((a, b) => b.count - a.count)[0] ?? { value: "-", count: 0, percent: 0 };

    return { labels, counts, total, rows, top };
  }, [data]);

  // Paleta generada
  const bg = labels.map((_, i) => {
    const r = 70 + ((i * 97) % 170);
    const g = 70 + ((i * 59) % 170);
    const b = 70 + ((i * 31) % 170);
    return `rgba(${r},${g},${b},0.6)`;
  });

  const chartData = {
    labels: labels.map((l, i) => {
      if (!showPercentInLegend) return l;
      const pct = total ? Math.round((counts[i] * 1000) / total) / 10 : 0;
      return `${l} (${pct}%)`;
    }),
    datasets: [
      {
        label: "Cantidad",
        data: counts,
        backgroundColor: chartType === "line" ? undefined : bg,
        borderColor: "rgba(0,0,0,0.25)",
        borderWidth: 1,
        fill: chartType === "line",
        tension: chartType === "line" ? 0.3 : 0,
        pointRadius: chartType === "line" ? 3 : 0,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: chartType === "pie" ? "top" : "bottom" },
      title: { display: true, text: sanitizedTitle },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const idx = ctx.dataIndex;
            const c = counts[idx] ?? 0;
            const pct = total ? ((c * 100) / total).toFixed(1) : "0.0";
            return `${labels[idx]}: ${c} (${pct}%)`;
          },
        },
      },
    },
    scales:
      chartType === "bar" || chartType === "line"
        ? {
            x: { ticks: { autoSkip: true, maxRotation: 30, minRotation: 0 } },
            y: { beginAtZero: true, precision: 0 },
          }
        : undefined,
  };

  const getChartComponent = () => {
    if (chartType === "pie") return <Pie ref={chartRef as any} data={chartData} options={options} />;
    if (chartType === "line") return <Line ref={chartRef as any} data={chartData} options={options} />;
    return <Bar ref={chartRef as any} data={chartData} options={options} />;
  };

  // ===== Exportaciones =====
  const exportPNG = () => {
    const c = chartRef.current as ChartJS | null;
    if (!c) return;
    const url = c.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileBase}.png`;
    link.click();
  };

  const exportExcel = () => {
    const pretty = rows.map((r) => ({ Valor: r.value, Cantidad: r.count, Porcentaje: `${r.percent.toFixed(1)}%` }));
    const ws = XLSX.utils.json_to_sheet(pretty);
    const maxLen = (key: keyof (typeof pretty)[number]) =>
      Math.max(...pretty.map((r) => String(r[key]).length), String(key).length) + 2;
    (ws as any)["!cols"] = [
      { wch: maxLen("Valor") },
      { wch: maxLen("Cantidad") },
      { wch: maxLen("Porcentaje") },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 12;

    doc.setFontSize(14);
    doc.text(sanitizedTitle || "Resultados", margin, 16);

    const c = chartRef.current as ChartJS | null;
    let startY = 24;
    if (c) {
      const img = c.toBase64Image();
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = imgWidth * 0.5;
      doc.addImage(img, "PNG", margin, startY, imgWidth, imgHeight);
      startY += imgHeight + 6;
    }

    doc.setFontSize(11);
    const topLine = `Más votado: ${top.value} — ${top.count} (${top.percent.toFixed(1)}%)`;
    doc.text(`Total respuestas: ${total}`, margin, startY);
    doc.text(topLine, margin, startY + 6);
    startY += 12;

    autoTable(doc, {
      startY,
      head: [["Valor", "Cantidad", "% del total"]],
      body: rows.map((r) => [r.value, r.count, `${r.percent.toFixed(1)}%`]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [33, 150, 243] },
      theme: "striped",
    });

    doc.save(`${fileBase}.pdf`);
  };

  return (
    <div className="card" style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,.08)" }}>
      <div className="card-body">
        <div className="d-flex gap-2 justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            {/* <label className="form-label m-0" style={{ fontSize: 12 }}>Tipo de gráfica:</label> */}
            <select
              className="form-select form-select-sm"
              value={chartType}
              onChange={(e) => setChartType(e.target.value as any)}
              style={{ width: 160 }}
            >
              <option value="bar">Barras</option>
              <option value="line">Líneas</option>
              <option value="pie">Pastel</option>
            </select>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={exportExcel} title="Exportar datos a Excel">
              Excel
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={exportPNG} title="Exportar gráfico como PNG">
              PNG
            </button>
            <button className="btn btn-sm btn-primary" onClick={exportPDF} title="Exportar PDF (gráfico + tabla)">
              PDF
            </button>
          </div>
        </div>

        <div style={{ height }}>{getChartComponent()}</div>

        {/* Resumen */}
        <div style={{ marginTop: 16 }}>
          <h5 style={{ marginBottom: 8 }}>Resumen</h5>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><strong>Total respuestas:</strong> {total.toLocaleString()}</li>
            <li>
              <strong>Más votado:</strong> {top.value} — {top.count.toLocaleString()} ({top.percent.toFixed(1)}%)
            </li>
          </ul>
        </div>

        {/* Tabla valor, cantidad, porcentaje */}
        <div style={{ marginTop: 16, overflowX: "auto" }}>
          <table className="table table-sm table-striped align-middle" style={{ minWidth: 420 }}>
            <thead>
              <tr>
                <th>Valor</th>
                <th className="text-end">Cantidad</th>
                <th className="text-end">% del total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.value}>
                  <td>{r.value}</td>
                  <td className="text-end">{r.count.toLocaleString()}</td>
                  <td className="text-end">{r.percent.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                <th className="text-end">{total.toLocaleString()}</th>
                <th className="text-end">100.0%</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SurveyResultsChart;
